"use client";

import React, { useEffect, useMemo, useReducer, useRef, useState } from "react";
import useSWR, { mutate } from "swr";
import Checkbox from "@/components/Elements/Checkbox";
import useUserStore from "@/stores/useUserStore";
import Skeleton from "react-loading-skeleton";
import { fetchNotifications } from "@/actions/user";
import UserNotifications from "./UserNotifications";
import { useShallow } from "zustand/react/shallow";
import { NotificationContext } from "./NotificationContext";
import useNotificationStore from "@/stores/useNotificationStore";
import "react-loading-skeleton/dist/skeleton.css";

interface PageNotification {
  id: number;
  action: string;
  alertAsNotification: boolean;
  created: string;
  data: {
    auctionItem: any;
  };
  isDismissable: boolean;
  meta: any;
  read: boolean;
  sticky: boolean;
  theme: string;
  type: string;
}

const initializeNotificationStore = (
  pageNotifications: PageNotification[],
  totalHits: number,
  unreadCount: number,
  allowedTypes: string[]
) => {
  const currentStore = useNotificationStore.getState().notificationData;

  // Only initialize if store is empty or this is the first load
  if (
    !currentStore ||
    !Array.isArray(currentStore?.notifications) ||
    currentStore.notifications.length === 0
  ) {
    const convertedNotifications = pageNotifications.map((pageNotif) => ({
      id: pageNotif.id.toString(),
      type: pageNotif.type,
      action: pageNotif.action,
      meta: pageNotif.meta,
      theme: pageNotif.theme,
      read: pageNotif.read,
      created: pageNotif.created,
      isDismissable: pageNotif.isDismissable,
      alertAsNotification: pageNotif.alertAsNotification,
      sticky: pageNotif.sticky,
      data: {
        auctionItem: pageNotif.data.auctionItem,
      },
    }));

    const newStore = {
      totalHits,
      notifications: convertedNotifications,
      unreadCount,
      allowedTypes,
    };

    useNotificationStore.setState({ notificationData: newStore });
  }
};

const Notifications = ({ block, blockStyle, extraClass }: BlockProps) => {
  const { user } = useUserStore(useShallow((state) => state));
  const userId = user?.id?.toString() || "";
  const { notificationData } = useNotificationStore();

  const [paginationArgs, setPaginationArgs] = useState<any>({
    page: 1,
    size: 5,
    total: 0,
  });

  const reducer = (state: any, action: any) => {
    const payload = action.payload;

    switch (action.type) {
      case "ADD_FILTER":
        return payload.map((newFilter: any) => {
          const existingFilter = state.find(
            (item: any) => item.settingsKey === newFilter.settingsKey
          );
          if (existingFilter) {
            return { ...existingFilter, ...newFilter };
          }

          return newFilter;
        });
      case "TOGGLE_FILTER":
        return state.map((item: any) =>
          item.settingsKey === action.settingsKey
            ? { ...item, selected: !item.selected }
            : item
        );
      default:
        return state;
    }
  };

  const [menuState, dispatchMenuState] = useReducer(reducer, []);

  const notificationParentClasses = menuState
    ?.filter((state: any) => state.selected)
    .map((state: any) => state.key);

  const fetchKey = [
    "user/getUserNotifications",
    userId,
    paginationArgs?.page,
    notificationParentClasses?.length,
  ];

  const {
    data: notificationsData,
    error,
    isLoading,
  } = useSWR<any, Error>(
    [fetchKey],
    async () => {
      if (!userId) {
        return null;
      }

      try {
        const data = await fetchNotifications({
          offset: (paginationArgs.page - 1) * paginationArgs.size,
          limit: paginationArgs.size,
          notificationParentClasses: notificationParentClasses?.length
            ? notificationParentClasses
            : [],
        });

        setPaginationArgs((prev: any) => ({
          ...prev,
          test: 2,
          total: data?.totalHits,
        }));

        return data;
      } catch (error) {
        console.error("Error fetching notifications", error);
      }

      return [];
    },
    {
      refreshInterval: 0,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 300000,
    }
  );

  const totalHits = notificationsData?.totalHits || 0;
  const allowedTypes = notificationsData?.allowedTypes || [];

  const filterSelections = [
    {
      settingsKey: "USER_OUTBIDDED_NOTIFICATION",
      label: "Budhändelser",
      key: "bid",
      selected: true,
    },
  ];

  const allowedTypesCompare = JSON.stringify(allowedTypes);

  useEffect(() => {
    if (isLoading || error) {
      return;
    }

    const newMenuState = filterSelections
      .filter((f) => allowedTypes.includes(f.settingsKey))
      .map((item) => {
        return {
          key: item.key,
          label: item.label,
          settingsKey: item.settingsKey,
          selected: item.selected,
        };
      });

    if (JSON.stringify(menuState) !== JSON.stringify(newMenuState)) {
      dispatchMenuState({ type: "ADD_FILTER", payload: newMenuState });
    }
  }, [allowedTypesCompare]);

  const { notifications, unreadCount } = notificationsData || {};

  useEffect(() => {
    if (notificationsData && notifications && Array.isArray(notifications)) {
      console.log("Raw notifications from API:", notifications);
      initializeNotificationStore(
        notifications,
        totalHits,
        unreadCount || 0,
        allowedTypes
      );
    }
  }, [
    notificationsData?.totalHits,
    notifications?.length,
    unreadCount,
    allowedTypes?.length,
  ]);

  // Use store data as primary source, fallback to SWR data for initial load
  const displayNotifications =
    notificationData?.notifications || notifications || [];
  const displayUnreadCount = notificationData?.unreadCount ?? unreadCount ?? 0;
  const displayTotalHits = notificationData?.totalHits ?? totalHits ?? 0;

  console.log("Display notifications:", displayNotifications);
  console.log("Notification store data:", notificationData);

  if (error) {
    return (
      <div>
        Kunde inte läsa in sidan, försök igen senare eller kontakta Tovek för
        support.
      </div>
    );
  }

  if (!userId) {
    return (
      <div className="flex flex-col space-y-8">
        <Skeleton height={100} containerClassName="flex-1" />
        <Skeleton height={100} containerClassName="flex-1" />
        <Skeleton height={100} containerClassName="flex-1" />
      </div>
    );
  }

  return (
    <NotificationContext.Provider
      value={{
        paginationArgs,
        setPaginationArgs,
        menuState,
        dispatchMenuState,
      }}
    >
      <div className="flex flex-col space-y-8 w-full" style={blockStyle}>
        <div className="flex flex-row items-center justify-between mb-2">
          <h2>Notiser & händelser</h2>
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-sm text-gray-600">Live uppdateringar</span>
            </div>
          </div>
        </div>
        <div className="justify-start items-start space-y-5 flex-col">
          {displayTotalHits > 0 && <span>Visa:</span>}
          {menuState?.map((filter: any, index: number) => {
            return (
              <div
                className="p-2 px-5 bg-aliceblue rounded-sm text-14 font-semibold text-black items-center justify-start space-x-3 flex"
                key={index}
              >
                <span>{filter.label}</span>
                <Checkbox
                  ripple={false}
                  checked={filter.selected}
                  onChange={() => {
                    dispatchMenuState({
                      type: "TOGGLE_FILTER",
                      settingsKey: filter.settingsKey,
                    });
                  }}
                />
              </div>
            );
          })}
        </div>
        {isLoading ? (
          <div className="flex flex-col space-y-4">
            <Skeleton height={30} containerClassName="flex-1" />
            <Skeleton height={30} containerClassName="flex-1" />
          </div>
        ) : (
          <UserNotifications
            notifications={displayNotifications}
            isLoading={isLoading}
          />
        )}
      </div>
    </NotificationContext.Provider>
  );
};

export default Notifications;
